import React, { useState, useEffect } from 'react';

const TOKEN_KEY = 'dev_docs_token';
const TABS = [
    { id: 'viewer', label: 'Architecture Viewer', src: '/api/arch/viewer/index.html' },
    { id: 'overview', label: '기술 아키텍처', src: '/api/arch/presentation/index.html' },
    { id: 'lessons', label: '팁 & 트러블슈팅', src: '/api/arch/presentation/lessons.html' },
    { id: 'claude', label: 'CLAUDE.md', src: null },
];

async function verifyToken(token) {
    const res = await fetch(`/api/dev/verify?token=${encodeURIComponent(token)}`);
    return res.ok;
}

function TokenGate({ onVerified }) {
    const [input, setInput] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        const ok = await verifyToken(input.trim());
        if (ok) {
            sessionStorage.setItem(TOKEN_KEY, input.trim());
            onVerified(input.trim());
        } else {
            setError('토큰이 올바르지 않습니다.');
        }
        setLoading(false);
    };

    return (
        <div style={{
            minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: '#0c0e16',
        }}>
            <form onSubmit={handleSubmit} style={{
                background: '#141928', border: '1px solid #252d3d', borderRadius: 12,
                padding: '40px 48px', display: 'flex', flexDirection: 'column', gap: 16, minWidth: 320,
            }}>
                <div style={{ color: '#e2e8f0', fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
                    Dev Docs
                </div>
                <div style={{ color: '#6b7a96', fontSize: 13 }}>액세스 토큰을 입력하세요</div>
                <input
                    type="password"
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    placeholder="token"
                    autoFocus
                    style={{
                        background: '#1a2035', border: '1px solid #252d3d', borderRadius: 8,
                        color: '#e2e8f0', padding: '10px 14px', fontSize: 14, outline: 'none',
                    }}
                />
                {error && <div style={{ color: '#EF4444', fontSize: 13 }}>{error}</div>}
                <button
                    type="submit"
                    disabled={loading || !input.trim()}
                    style={{
                        background: '#8B5CF6', color: '#fff', border: 'none', borderRadius: 8,
                        padding: '10px 0', fontWeight: 600, fontSize: 14, cursor: 'pointer',
                        opacity: loading || !input.trim() ? 0.5 : 1,
                    }}
                >
                    {loading ? '확인 중...' : '확인'}
                </button>
            </form>
        </div>
    );
}

function ClaudeMdViewer() {
    const [content, setContent] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetch('/api/arch/CLAUDE.md')
            .then(r => r.text())
            .then(t => { setContent(t); setLoading(false); })
            .catch(() => { setContent('로드 실패'); setLoading(false); });
    }, []);

    if (loading) return <div style={{ color: '#a8b5c8', padding: 32 }}>로딩 중...</div>;

    return (
        <div style={{
            height: '100%', overflowY: 'auto', padding: '32px 40px',
            background: '#0c0e16', color: '#e2e8f0', fontFamily: 'monospace',
            fontSize: 13, lineHeight: 1.8, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}>
            {content}
        </div>
    );
}

export function DevDocs() {
    const [verified, setVerified] = useState(false);
    const [checking, setChecking] = useState(true);
    const [activeTab, setActiveTab] = useState('viewer');

    useEffect(() => {
        // URL 쿼리 파라미터 우선, 없으면 sessionStorage
        const params = new URLSearchParams(window.location.search);
        const urlToken = params.get('token');
        const storedToken = sessionStorage.getItem(TOKEN_KEY);
        const token = urlToken || storedToken;

        if (!token) { setChecking(false); return; }

        verifyToken(token).then(ok => {
            if (ok) {
                sessionStorage.setItem(TOKEN_KEY, token);
                setVerified(true);
                // URL에서 token 파라미터 제거 (히스토리에 남지 않게)
                if (urlToken) {
                    const url = new URL(window.location.href);
                    url.searchParams.delete('token');
                    window.history.replaceState({}, '', url.toString());
                }
            } else {
                sessionStorage.removeItem(TOKEN_KEY);
            }
            setChecking(false);
        });
    }, []); // eslint-disable-line

    const handleVerified = (t) => { sessionStorage.setItem(TOKEN_KEY, t); setVerified(true); };

    if (checking) return (
        <div style={{ minHeight: '100vh', background: '#0c0e16', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ color: '#6b7a96' }}>확인 중...</div>
        </div>
    );

    if (!verified) return <TokenGate onVerified={handleVerified} />;

    const activeTabInfo = TABS.find(t => t.id === activeTab);

    return (
        <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#0c0e16' }}>
            {/* Tab bar */}
            <div style={{
                display: 'flex', alignItems: 'center', gap: 4, padding: '8px 16px',
                background: '#141928', borderBottom: '1px solid #252d3d', flexShrink: 0,
            }}>
                <span style={{ color: '#6b7a96', fontSize: 12, fontWeight: 700, marginRight: 12 }}>DEV DOCS</span>
                {TABS.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        style={{
                            background: activeTab === tab.id ? '#252d3d' : 'none',
                            border: activeTab === tab.id ? '1px solid #2e3a52' : '1px solid transparent',
                            color: activeTab === tab.id ? '#e2e8f0' : '#6b7a96',
                            borderRadius: 6, padding: '5px 14px', fontSize: 13,
                            cursor: 'pointer', fontWeight: activeTab === tab.id ? 600 : 400,
                        }}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Content */}
            <div style={{ flex: 1, overflow: 'hidden' }}>
                {activeTabInfo?.src ? (
                    <iframe
                        key={activeTab}
                        src={activeTabInfo.src}
                        title={activeTabInfo.label}
                        style={{ width: '100%', height: '100%', border: 'none' }}
                        sandbox="allow-scripts allow-same-origin"
                    />
                ) : (
                    <ClaudeMdViewer />
                )}
            </div>
        </div>
    );
}
